import { config } from "~/config.ts"
import { APP_ROUTES, APPLICATION_BASE } from "~/router.tsx"
import { getLocation, isRootRelativePath, redirectTo, SESSION_STORAGE_KEYS, SessionStorage } from "~/utils"

const AUTHORIZE_ENDPOINT = `${config.singleSignOnUrl}/oauth2/authorize`

const AUTHORIZATION_SCOPE = "vast vast-api country currency marketplace offline_access"

export const RANDOM_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"

export type OauthOptions = {
  replaceCurrentHistoryEntry?: boolean
}

export const startOAuthFlow = async ({ replaceCurrentHistoryEntry }: OauthOptions = {}) => {
  // Generate a random string of specified length using characters from `randomChars`
  const generateRandomString = (length: number) =>
    Array.from({ length }, () => RANDOM_CHARS.charAt(Math.floor(Math.random() * 62))).join("")

  // Generate a code challenge from a code verifier using SHA-256 hashing
  const generateCodeChallenge = async (codeVerifier: string) => {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier))
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/=/g, "")
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
  }

  // Generate the code verifier (random string of 64 characters)
  const codeVerifier = generateRandomString(64)
  // Determine the code challenge method (use "S256" if `crypto.subtle` is available)
  const challengeMethod = crypto.subtle ? "S256" : "plain"
  // Generate the code challenge (hashed value) or use the plain verifier
  const codeChallenge = challengeMethod === "S256" ? await generateCodeChallenge(codeVerifier) : codeVerifier

  // Store the code verifier in session storage for later use
  SessionStorage.setItem({ key: SESSION_STORAGE_KEYS.CODE_VERIFIER, value: codeVerifier })

  const location = getLocation()
  const redirectUri = `${location.origin}${APPLICATION_BASE}/${APP_ROUTES.SignIn}`

  const pathnameWithQuery = location.pathname + location.search

  // Construct the URL parameters for the authorization request
  const args = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    code_challenge_method: challengeMethod,
    code_challenge: codeChallenge,
    redirect_uri: redirectUri,
    return_to: isRootRelativePath(pathnameWithQuery) ? pathnameWithQuery : `${APPLICATION_BASE}/`,
    scope: AUTHORIZATION_SCOPE,
  })

  // Store current pathname to redirect to it after the authentication is completed
  let pathName = location.pathname
  if (pathName.startsWith(APPLICATION_BASE)) {
    pathName = pathName.replace(APPLICATION_BASE, "")
  }
  SessionStorage.setItem({
    key: SESSION_STORAGE_KEYS.AFTER_AUTH_REDIRECT_URL,
    value: pathName + location.search,
  })

  // the full authorization URL with query parameters
  const authorizationUrl = `${AUTHORIZE_ENDPOINT}?${args}`

  redirectTo(authorizationUrl, replaceCurrentHistoryEntry || false)
}
